import toast from 'react-hot-toast';
import { Check, X } from 'lucide-react';

type ToastType = 'success' | 'error';

const toastBase =
  'pointer-events-auto flex items-start gap-2.5 rounded-lg bg-white border border-trend-sand px-4 py-3 text-sm text-trend-ink shadow-lg max-w-[min(100vw-2rem,22rem)]';

// Brand-aligned accents so toasts sit inside the site's warm palette instead of
// a generic dark pill: sage (the site green) for success, clay (the site's warm
// red/terracotta) for errors.
const iconStyles: Record<ToastType, { icon: string; chip: string }> = {
  success: { icon: 'text-trend-sage', chip: 'bg-trend-sage-light' },
  error: { icon: 'text-trend-clay', chip: 'bg-trend-clay/10' },
};

// Coerce anything into a renderable string so a non-string message (e.g. an API
// validation error object/array) can never crash the toast/render tree.
function toMessageString(message: unknown): string {
  if (typeof message === 'string') return message;
  if (message == null) return '';
  if (Array.isArray(message)) {
    return message
      .map((m) => (typeof m === 'string' ? m : (m as any)?.msg))
      .filter(Boolean)
      .join(', ');
  }
  if (typeof message === 'object') {
    const m = message as any;
    return m.msg || m.message || m.detail || 'Something went wrong';
  }
  return String(message);
}

function ToastBody({ type, message }: { type: ToastType; message: string }) {
  const Icon = type === 'success' ? Check : X;
  const { icon, chip } = iconStyles[type];
  return (
    <div className={toastBase}>
      <span className={`mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full ${chip}`}>
        <Icon className={`h-3.5 w-3.5 ${icon}`} strokeWidth={2.5} />
      </span>
      <span className="font-medium leading-snug">{toMessageString(message)}</span>
    </div>
  );
}

function show(type: ToastType, message: string, duration = type === 'error' ? 4500 : 3000) {
  toast.custom(
    (t) => (
      <div
        className={`transition-all duration-300 ease-out ${
          t.visible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-2 scale-[0.98]'
        }`}
      >
        <ToastBody type={type} message={message} />
      </div>
    ),
    { duration }
  );
}

export const appToast = {
  success: (message: string) => show('success', message),
  error: (message: string) => show('error', message),
};
